import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  ArrowRight, Hammer, Home, TrendingUp, RefreshCw, Wallet, Building2, Users,
} from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();

const cycle = [
  { icon: Building2, title: "Buy", desc: "The fund acquires undervalued properties in target neighborhoods." },
  { icon: Hammer, title: "Rehab", desc: "Each home is renovated to modern, market-ready condition." },
  { icon: TrendingUp, title: "Sell 10", desc: "Ten rehabbed homes are sold, realizing profit for the fund." },
  { icon: Home, title: "Retain the 11th", desc: "Every 11th home is kept as a long-term rental for recurring income." },
];

export default function Landing() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/fund/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const pct = stats ? Math.min(100, stats.current_house_progress) : 0;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A2E26]/5 text-[#0A2E26] text-xs font-semibold tracking-wide uppercase">
              <Users className="w-3.5 h-3.5" /> 2,000 members · $100 each
            </span>
            <h1 className="mt-6 font-display font-extrabold tracking-tight text-[#0A2E26] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Own a piece of<br />real estate,<br />
              <span className="text-[#C49A45]">$100 at a time.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C5C5C] max-w-md leading-relaxed">
              We're rallying our first <span className="font-semibold text-[#0A2E26]">2,000 members</span> to
              fund the first house acquisition and rehab budget. Buy, rehab and sell
              homes together — profits paid out yearly, or reinvested to compound your share.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-invest-btn">
                <Button className="bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-12 px-7 text-base gap-2">
                  Invest $100 <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/properties" data-testid="hero-portfolio-btn">
                <Button variant="outline" className="border-[#0A2E26]/20 text-[#0A2E26] rounded-md h-12 px-7 text-base">
                  View the portfolio
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-sm">
              <img
                src="https://images.pexels.com/photos/8134821/pexels-photo-8134821.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Contemporary home"
                className="w-full h-[420px] object-cover"
              />
            </div>
            {/* Raise progress card */}
            <div className="absolute -bottom-6 -left-2 sm:left-6 bg-white border border-[#E5E5E5] rounded-xl p-5 w-64 shadow-md">
              <p className="text-xs uppercase tracking-wide text-[#5C5C5C] font-semibold">Capital raised</p>
              <p className="font-metric text-4xl text-[#0A2E26] mt-1">
                {stats ? fmt(stats.capital_raised) : "$0"}
              </p>
              <div className="mt-3 h-2 rounded-full bg-[#F9F8F6] overflow-hidden">
                <div className="h-full bg-[#C49A45] rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-[#5C5C5C] mt-2">
                of {stats ? fmt(stats.target_capital) : "$200,000"} goal
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-[#E5E5E5] bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Members target", value: stats ? stats.target_members.toLocaleString() : "2,000" },
            { label: "Current investors", value: stats ? stats.investors.toLocaleString() : "0" },
            { label: "Homes in portfolio", value: stats ? stats.houses_total : "0" },
            { label: "Homes sold", value: stats ? stats.houses_sold : "0" },
          ].map((s, i) => (
            <div key={i} className="py-8 px-4 border-r border-[#E5E5E5] last:border-r-0">
              <p className="font-metric text-4xl text-[#0A2E26]">{s.value}</p>
              <p className="text-sm text-[#5C5C5C] mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FIRST ACQUISITION TRACKER */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="first-acquisition-tracker">
        <div className="bg-[#0A2E26] noise-overlay rounded-3xl p-8 sm:p-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C49A45]/20 text-[#C49A45] text-xs font-bold tracking-widest uppercase" data-testid="acquisition-label">
                House #{stats ? stats.current_house_number : 1} · {stats && stats.houses_funded > 0 ? "Next Acquisition" : "First Acquisition"}
              </span>
              <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
                {stats && stats.houses_funded > 0
                  ? `Funding home #${stats.current_house_number}`
                  : "Funding our first home"}
              </h2>
              <p className="mt-2 text-white/60 max-w-md">
                {stats && stats.houses_funded > 0
                  ? `${stats.houses_funded} home${stats.houses_funded === 1 ? "" : "s"} funded so far. Every $${(stats.house_goal).toLocaleString()} funds the next acquisition + rehab.`
                  : "When 2,000 members join, the fund buys and rehabs house #1. Here's how close we are."}
              </p>
              {stats && (
                <p className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-white/80 bg-white/10 rounded-full px-3 py-1" data-testid="cycle-progress">
                  Cycle {stats.cycle_number} · {stats.cycle_sold} of 10 homes sold, then retain the 11th as a rental
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="font-metric text-6xl sm:text-7xl text-[#C49A45] leading-none" data-testid="acquisition-percent">
                {Math.round(pct)}%
              </p>
              <p className="text-white/60 text-sm mt-1">of home #{stats ? stats.current_house_number : 1} funded</p>
            </div>
          </div>

          {/* Big progress bar */}
          <div className="mt-8 h-5 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#C49A45] to-[#e0bd6e]"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              data-testid="acquisition-bar"
            />
          </div>

          {/* Rolling house milestones */}
          {stats && (
            <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="house-milestones">
              {[0, 1, 2, 3, 4].map((i) => {
                const houseNum = i + 1;
                const funded = houseNum <= stats.houses_funded;
                const current = houseNum === stats.current_house_number;
                return (
                  <span key={i}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                      funded ? "bg-[#C49A45] text-[#0A2E26] border-[#C49A45]"
                      : current ? "bg-white/10 text-white border-[#C49A45]"
                      : "bg-transparent text-white/40 border-white/15"}`}>
                    House #{houseNum} {funded ? "✓ funded" : current ? "· funding now" : ""}
                  </span>
                );
              })}
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "Raised for this home", value: stats ? fmt(stats.current_house_raised) : "$0", sub: `of ${stats ? fmt(stats.house_goal) : "$200,000"}` },
              { label: "Members joined", value: stats ? stats.investors.toLocaleString() : "0", sub: `of ${stats ? stats.target_members.toLocaleString() : "2,000"} per home` },
              { label: "Still needed", value: stats ? fmt(stats.current_house_remaining) : "$200,000", sub: `to fund home #${stats ? stats.current_house_number : 1}` },
            ].map((c, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`acquisition-stat-${i}`}>
                <p className="text-xs uppercase tracking-wide text-white/50 font-semibold">{c.label}</p>
                <p className="font-metric text-4xl text-white mt-2">{c.value}</p>
                <p className="text-white/50 text-sm mt-1">{c.sub}</p>
              </div>
            ))}
          </div>

          <Link to="/register" data-testid="acquisition-cta">
            <Button className="mt-8 bg-[#C49A45] hover:bg-[#b0863a] text-[#0A2E26] font-semibold rounded-md h-12 px-8">
              Claim your spot — $100
            </Button>
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2E26] tracking-tight">
            A disciplined cycle, repeated.
          </h2>
          <p className="mt-4 text-lg text-[#5C5C5C]">
            The general fund runs in structured cycles. Sell ten, keep one for rental income —
            then do it all again.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cycle.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white border border-[#E5E5E5] rounded-xl p-6 hover:-translate-y-1 hover:shadow-sm transition-transform"
            >
              <div className="w-11 h-11 rounded-lg bg-[#0A2E26] flex items-center justify-center">
                <c.icon className="w-5 h-5 text-[#C49A45]" />
              </div>
              <p className="mt-4 text-xs font-semibold text-[#C49A45] tracking-wide">STEP {i + 1}</p>
              <h3 className="font-display font-bold text-xl text-[#0A2E26] mt-1">{c.title}</h3>
              <p className="text-sm text-[#5C5C5C] mt-2 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* RETURNS */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-8">
            <Wallet className="w-8 h-8 text-[#059669]" />
            <h3 className="font-display font-bold text-2xl text-[#0A2E26] mt-4">Take it yearly</h3>
            <p className="text-[#5C5C5C] mt-3 leading-relaxed">
              Each year, realized profit from home sales is distributed to members
              per share held. Withdraw it straight to your balance.
            </p>
          </div>
          <div className="bg-[#0A2E26] noise-overlay rounded-xl p-8">
            <RefreshCw className="w-8 h-8 text-[#C49A45]" />
            <h3 className="font-display font-bold text-2xl text-white mt-4">Or reinvest & compound</h3>
            <p className="text-white/70 mt-3 leading-relaxed">
              Flip on auto-reinvest and your yearly returns buy more shares
              automatically — growing your stake in every future cycle.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A2E26] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tight">
            Join the fund today.
          </h2>
          <p className="text-white/70 mt-4 max-w-lg mx-auto">
            One share is $100. Buy as many as you like and start earning from real, tangible assets.
          </p>
          <Link to="/register" data-testid="cta-register-btn">
            <Button className="mt-8 bg-[#C49A45] hover:bg-[#b0863a] text-[#0A2E26] font-semibold rounded-md h-12 px-8 text-base">
              Become a Member
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E5E5] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-[#5C5C5C] flex justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} EquiBrick Fund</span>
          <span data-testid="footer-management">Properties managed by CMT Home and Property Solutions LLC</span>
          <span>Investments carry risk. This is a demo application.</span>
        </div>
      </footer>
    </div>
  );
}
